"use client";

import { FileSearch } from "lucide-react";
import { useEffect, useState } from "react";
import { ZoneLink } from "@/components/layout/zone-link";
import { useAnalytics } from "@/lib/analytics/hooks";
import type { Locale } from "@/lib/i18n/config";
import { localePath } from "@/lib/i18n/link-utils";

import { FREE_CV_REVIEW_FLAG } from "@/lib/tools/free-cv-review";

export { FREE_CV_REVIEW_FLAG };
export const CV_REVIEW_ENTRY_CLICKED_EVENT = "cv_review_entry_clicked";

export type CvReviewEntrySurface = "job_detail" | "hunt" | "resume_list";

const COPY = {
	id: {
		job_detail: "Cek dulu CV kamu, gratis, sebelum melamar",
		hunt: "Review CV gratis: lihat baris mana yang perlu diperbaiki",
		resume_list: "Review CV gratis: lihat baris mana yang perlu diperbaiki",
	},
	en: {
		job_detail: "Check your CV for free before you apply",
		hunt: "Free CV review: see which lines to fix",
		resume_list: "Free CV review: see which lines to fix",
	},
} as const;

/** Same cookie name and `flag=variant` token syntax as src/lib/flags/preview-force.ts. */
const PREVIEW_FORCE_COOKIE = "ld_preview_force";

/**
 * Local e2e only: `next dev` (NODE_ENV "development") honours the
 * `ld_preview_force` cookie the server-side flag readers use, because
 * posthog-js never initialises on localhost. Production builds inline
 * NODE_ENV as "production" and drop this branch, so PostHog stays the sole
 * switch there. Parsed inline (not via preview-force) to keep the client
 * bundle off that server-side module.
 */
function devForcedFlag(): boolean | null {
	if (process.env.NODE_ENV !== "development") return null;
	if (typeof document === "undefined") return null;
	const raw = document.cookie
		.split("; ")
		.find((c) => c.startsWith(`${PREVIEW_FORCE_COOKIE}=`))
		?.slice(PREVIEW_FORCE_COOKIE.length + 1);
	if (!raw) return null;
	for (const token of decodeURIComponent(raw).split(",")) {
		const [key, variant] = token.trim().split("=");
		if (key === FREE_CV_REVIEW_FLAG && variant) return variant === "on";
	}
	return null;
}

/**
 * Entry point into the free CV review (`/tools/cek-ats-cv`) from where seekers
 * already are (owner 2026-10-30: "make this discoverable"). Renders nothing
 * until the `free-cv-review` flag reads ON for this visitor, client-side, so a
 * cacheable page (job detail) stays byte-identical for everyone.
 */
export function CvReviewEntry({
	surface,
	locale,
	className = "",
	initialEnabled = null,
}: {
	surface: CvReviewEntrySurface;
	locale: Locale;
	className?: string;
	/** Server-side per-user read of the same flag, for surfaces that render
	 * per user anyway (/seeker). Puts the link in the first paint instead of
	 * popping it in once PostHog loads (a /seeker load-time layout shift,
	 * PostHog web vitals 2026-10-18..31). Omit on cacheable pages. */
	initialEnabled?: boolean | null;
}) {
	const { posthog } = useAnalytics();
	const [enabled, setEnabled] = useState<boolean | null>(initialEnabled);
	useEffect(() => {
		const forced = devForcedFlag();
		if (forced !== null) {
			setEnabled(forced);
			return;
		}
		if (!posthog) return;
		const check = () => {
			const value = posthog.getFeatureFlag(FREE_CV_REVIEW_FLAG);
			// `undefined` = flags not loaded yet: keep the server answer.
			if (value !== undefined) setEnabled(value === true);
		};
		check();
		return posthog.onFeatureFlags(check);
	}, [posthog]);
	if (enabled !== true) return null;

	const label = COPY[locale === "en" ? "en" : "id"][surface];
	return (
		<ZoneLink
			href={`${localePath(locale, "/tools/cek-ats-cv")}?src=${surface}`}
			data-testid={`cv-review-entry-${surface}`}
			onClick={() =>
				posthog?.capture(
					CV_REVIEW_ENTRY_CLICKED_EVENT,
					{ surface, locale },
					{ transport: "sendBeacon" },
				)
			}
			className={`flex items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
		>
			<span className="flex items-center gap-2">
				<FileSearch
					aria-hidden="true"
					className="size-4 shrink-0 text-primary"
				/>
				{label}
			</span>
			<span aria-hidden="true" className="shrink-0 text-primary">
				→
			</span>
		</ZoneLink>
	);
}
